import { HgSupportedLanguage } from '@smg-real-estate/domus';
import type { GeoLocation, Locale } from '@smg-real-estate/geo-libs';
import { defineStore } from 'pinia';

import { fetchAgents } from '@/api/agentMetaApi';
import { DEFAULT_AGENCY_PAGE_LIST_SIZE } from '@/api/customerMetaApi';
import {
  buildFacetQuery,
  emptyAgentSearchFacets,
  toRequestFacets,
  type AgentSearchFacets,
} from '@/helpers/AgentDirectory/agentFacetRouteParams';
import {
  AGENT_DIRECTORY_STASH_QUERY_KEYS,
  pickDirectoryStashQueryFromRoute,
  type AgentDirectoryStashQuery,
} from '@/helpers/Directory/directoryToggleRouteQuery';
import {
  applySelectedLocationsToRoute,
  getLocationGeoId,
  haveSameSearchedGeoIds,
  resolveGeoLocations,
} from '@/stores/geoLocationHelpers';
import { LoadState } from '@/stores/types';
import type { AgentProfile } from '@/types/agentProfile';
import type { LocationQuery } from 'vue-router';

export interface AgentProfilesState {
  agents: AgentProfile[];
  agentsLoadState: LoadState;
  totalAgentsCount: number;
  agentListCurrentPage: number;
  /** `null` instead of `undefined` so the key survives SSR state serialization and stays reactive after hydration. */
  searchedLocations: GeoLocation[] | null;
  /** True after leaving the page until the next route sync confirms or replaces `searchedLocations`. */
  isSearchedLocationsStale: boolean;
  /* selectedLocations represent location autocomplete input state (drives nextPageRoute) */
  selectedLocations: GeoLocation[];
  searchedFacets: AgentSearchFacets;
  /** Agent-only query keys preserved for agent ↔ agency tab switches. */
  lastAgentDirectoryQuery: AgentDirectoryStashQuery;

  /**
   * The guard to avoid race conditions when loading agents.
   */
  agentsRequestGeneration: number;
}

export const useAgentProfilesStore = defineStore('agentProfiles', {
  state: (): AgentProfilesState => ({
    agents: [],
    agentsLoadState: LoadState.UNDEFINED,
    totalAgentsCount: 0,
    agentListCurrentPage: 1,
    searchedLocations: null,
    isSearchedLocationsStale: false,
    selectedLocations: [],
    searchedFacets: emptyAgentSearchFacets(),
    lastAgentDirectoryQuery: {},
    agentsRequestGeneration: 0,
  }),
  actions: {
    /**
     * Loads the agents for the current page.
     * @param currentPage - The current page number (1-based index)
     */
    async loadAgents(currentPage = 1, requestGeneration?: number) {
      const generation = requestGeneration ?? ++this.agentsRequestGeneration;

      this.agents = [];
      this.agentsLoadState = LoadState.LOADING;
      const geoLocationIds = (this.searchedLocations ?? [])
        .map((location) => getLocationGeoId(location) ?? '')
        .filter((id) => !!id);

      try {
        const agents = await fetchAgents({
          from: (currentPage - 1) * DEFAULT_AGENCY_PAGE_LIST_SIZE,
          geoLocationIds,
          facets: toRequestFacets(this.searchedFacets),
        });

        if (generation !== this.agentsRequestGeneration) {
          // Abort mutating the state if the stale request comes later
          return;
        }

        this.agents = agents.records;
        this.totalAgentsCount = agents.total;
        this.agentsLoadState = LoadState.LOADED;
      } catch (error) {
        if (generation !== this.agentsRequestGeneration) {
          return;
        }

        this.agentsLoadState = LoadState.FAILED;
      }
    },
    /**
     * Keeps the loaded search for a later visit, but holds back FeaturedPlacement until the next
     * route sync, so it does not mount with geo ids that are about to change.
     */
    markSearchedLocationsStale() {
      this.agentsRequestGeneration++;
      this.isSearchedLocationsStale = true;
    },
    beginAgentRouteSync(): number {
      return ++this.agentsRequestGeneration;
    },
    syncLastAgentDirectoryQueryFromRoute(query: LocationQuery) {
      this.lastAgentDirectoryQuery = pickDirectoryStashQueryFromRoute(
        query,
        AGENT_DIRECTORY_STASH_QUERY_KEYS,
      );
    },
    async applyUrlParamsFilters({
      geoLocations,
      locale,
      currentPage,
      requestGeneration,
      resolvedLocations: resolvedLocationsParam,
      facets,
    }: {
      geoLocations: string[];
      locale: HgSupportedLanguage;
      currentPage: number;
      requestGeneration?: number;
      resolvedLocations?: GeoLocation[];
      facets?: AgentSearchFacets;
    }) {
      const generation = requestGeneration ?? ++this.agentsRequestGeneration;

      try {
        const resolvedLocations =
          resolvedLocationsParam ?? (await resolveGeoLocations(geoLocations, locale));

        if (generation !== this.agentsRequestGeneration) {
          return;
        }

        // The route is the source of truth for the agent directory:
        // an empty location param means a Switzerland-wide search.
        // Only touch searchedLocations when geo changes so FeaturedPlacement refetches then only.
        if (
          this.searchedLocations === null ||
          !haveSameSearchedGeoIds(this.searchedLocations, resolvedLocations)
        ) {
          this.searchedLocations = resolvedLocations;
        }
        this.isSearchedLocationsStale = false;
        this.selectedLocations = resolvedLocations;
        this.searchedFacets = facets ?? emptyAgentSearchFacets();
        this.agentListCurrentPage = currentPage;

        await this.loadAgents(currentPage, generation);
      } catch (error) {
        console.error(error);
      }
    },
    changeSelectedLocations(geoLocations: GeoLocation[]) {
      this.selectedLocations = geoLocations;
    },
    setRouteResolvedLocations(resolvedLocations: GeoLocation[], facets?: AgentSearchFacets) {
      // searchedLocations is kept as-is: a new reference recomputes searchedGeoIds and
      // makes FeaturedPlacement refetch, which can return a different featured agency.
      this.selectedLocations = resolvedLocations;
      this.isSearchedLocationsStale = false;
      if (facets) {
        this.searchedFacets = facets;
      }
    },
  },
  getters: {
    pageCount: (state): number => Math.ceil(state.totalAgentsCount / DEFAULT_AGENCY_PAGE_LIST_SIZE),
    nextPageRoute: (state) => {
      return (page: number, locale: Locale) => {
        const nextRoute: {
          params: { location: string | undefined };
          query: {
            page?: number;
            loc?: string;
            services?: string;
            specializations?: string;
          };
        } = {
          params: { location: undefined },
          query: {
            ...(page > 1 ? { page } : {}),
            ...buildFacetQuery(state.searchedFacets),
          },
        };
        return applySelectedLocationsToRoute(nextRoute, state.selectedLocations, locale);
      };
    },
    searchedGeoIds: (state) =>
      state.searchedLocations
        ?.map((location) => getLocationGeoId(location) ?? '')
        .filter((id) => !!id),
  },
});
